import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTnC } from "../../hooks/useTnC";

import { Box, Typography, Button, TextField } from "@mui/material";
import ChangeLanguage from "../../components/ChangeLanguage";
import { ENV } from "../../configs/env";
import { loyaltySubmit } from "../../services/ads/ads.service";
import AuthenticateLayout from "../../components/AuthenticateLayout";
import { getValueByLanguage } from "../../utils/utils";
import { Footer } from "../../components/Footer";


export default function LoyaltySubmit() {

  const location = useLocation();

  const { t, i18n } = useTranslation();
  const {
    advertiserBanner,
    loyaltyMemberId,
    languagesData,
    engageSession,
    isLoyaltySubmitted,
    reward_banner,
    couponInstruction,
    banner_image_corner,
  } = location.state || {};

  const { accepted, setAccepted } = useTnC(false);

  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState<string>("");
  const [hasFocused, setHasFocused] = useState(false);


  const rewardSummaryImage = getValueByLanguage(languagesData, "reward_summary_image");

  useEffect(() => {
    if (isLoyaltySubmitted) {
      setIsSuccess(true);
    }
    else if (loyaltyMemberId && loyaltyMemberId.length == 16) {
      const formattedCode = formatInput(loyaltyMemberId);
      setCode(formattedCode);
    }
  }, [isLoyaltySubmitted, loyaltyMemberId]);

  const formatInput = (value: string) => {
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    const trimmed = cleaned.slice(0, 16);
    const groups = trimmed.match(/.{1,4}/g) || [];
    return groups.join("-");
  };

  const handleFocus = () => {
    if (!hasFocused) {
      setHasFocused(true);
      setTimeout(() => {
        const el = inputRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(0, 0);
        }
      }, 0);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const originalCursor = input.selectionStart ?? 0;

    const rawValue = input.value.trim();
    const allowedPrefixes = ["6502"];

    const firstFour = rawValue.length >= 4 ? Number(rawValue.slice(0, 4)) : null;

    const isValid = allowedPrefixes.some(prefix => rawValue.startsWith(prefix)) || (firstFour !== null && firstFour >= 8000 && firstFour <= 8999);

    setErrorMessages(
      rawValue.length >= 4 && !isValid ? "enter_valid_tokyu_point_id" : ""
    );

    const formatted = formatInput(rawValue);

    // Set formatted value
    setCode(formatted);

    // Use setTimeout to wait for DOM to update
    setTimeout(() => {
      const inputElement = input; // still the same element
      if (inputElement) {
        // Calculate new cursor position
        let newCursor = originalCursor;

        // Count dashes before and after formatting
        const dashesBefore = (rawValue.slice(0, originalCursor).match(/-/g) || []).length;
        const dashesAfter = (formatted.slice(0, originalCursor).match(/-/g) || []).length;

        // Adjust cursor if dashes were added or removed
        newCursor += dashesAfter - dashesBefore;

        inputElement.setSelectionRange(newCursor, newCursor);
      }
    }, 0);
  };


  const handleSubmit = () => {
    if (code && engageSession) {
      const cleanCode = code.replace(/-/g, "");
      loyaltySubmit({ memberId: cleanCode, engageSessionId: engageSession })
        .then((status) => {
          if (status === 200) {
            setIsSuccess(true);
          }
          else {
            setErrorMessages("sumbmissionFailed");
          }
        })
        .catch((error) => {
          console.log("Error submitting loyalty");
          setErrorMessages("sumbmissionFailed");
        });
    }
  };

  const inputContainerWidth = { xs: "90vw", sm: "70vw", md: "50vw" };

  const greenButtonStyles = {
    borderRadius: "8px",
    padding: "13px",
    fontSize: "15px",
    fontWeight: 600,
    backgroundColor: "#10B981",
    color: "#ffffff",
    textTransform: "none",
    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
    "&:hover": {
      backgroundColor: "#0e9e6e",
      boxShadow: "0 4px 12px rgba(16, 185, 129, 0.4)",
    },
  };

  const inputTextStyle: React.CSSProperties = {
    width: "100%",
    height: "50px",
    fontSize: "16px",
    textAlign: "center",
    borderRadius: "8px",
    border: "1px solid #ccc",
    padding: "12px",
    letterSpacing: "2px",
    boxSizing: "border-box",
  };

  const renderErrorMessage = () => {
    if (!errorMessages) return null;
    return (
      <Box sx={{
        position: "relative",
        top: "10px",
        left: 0,
        right: 0,
        width: "100%",
        paddingLeft: "18px",
        paddingRight: "18px",
        textAlign: "center",
      }}>
        <Typography variant="body1" color="red">
          {errorMessages ? t(errorMessages) : ""}
        </Typography>
      </Box>
    );
  };

  const userActionsUi = (
    <>
      {/* <Box sx={{ height: 16, }} /> */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 1200,
          padding: "16px 0px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        {/* Inputs */}
        <Box
          sx={{
            width: inputContainerWidth,
            mt: 0,
            mb: 0
          }}
        >
          {i18n.language === "ja" && (<label
            htmlFor="code-input"
            style={{
              fontSize: "15px",
              fontWeight: 400,
              color: "#333",
              marginBottom: "4px",
              display: "block"
            }}
          >
            TOKYU POINT CARD番号
          </label>)}
          <input
            type="text"
            value={code}
            onChange={handleChange}
            onFocus={handleFocus}
            placeholder="XXXX-XXXX-XXXX-XXXX"
            style={inputTextStyle}
          />
        </Box>

        {/* Error Message */}
        {renderErrorMessage()}

        {/* Submit Button */}
        <Box
          sx={{
            width: inputContainerWidth,
            mt: 2,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={greenButtonStyles}
            onClick={handleSubmit}
            disabled={!!errorMessages || !code || code.length !== 19 || !accepted}

          >
            {t("submitThisMemberid")}
          </Button>
        </Box>
      </Box>
    </>
  );


  return (
    <>
      <AuthenticateLayout
        button={() => !ENV.HIDE_TRANSLATOR && <ChangeLanguage />}
        isLoading={false}
        background={reward_banner} // Rounded image corner not applicable to Coupon's Reward Banner Image.
        hideLogo={ENV.HIDE_LOGO}
      >
        {
          couponInstruction && !isSuccess
            ? <img
              src={couponInstruction}
              alt="icon"
              className="banner"
              style={{ width: "100%", display: "block" }}
            />
            : null
        }
        <div style={{ height: 100 }} />
        <div style={{ position: "sticky", bottom: 20 }}>

          {/* Sticky Action UI */}
          {!isSuccess && userActionsUi}

          {/* Success Message */}
          {isSuccess &&
            <Box sx={{ textAlign: "center", mt: 2, px: 1 }}>
              <Typography variant="h6" color="#b4b5b4" fontSize={i18n.language === "ja" ? 20:30} mb={3}>
                {t("thankYou")}
              </Typography>
              <Typography variant="h6" color="#b4b5b4" style={{ whiteSpace: "pre-line" }}>
                {t("yourPointShallBeAwarded")}
              </Typography>
              <p style={{ padding: 20 }}>
                <a href="https://www.topcard.co.jp/">https://www.topcard.co.jp/</a>
              </p>
            </Box>}
        </div>
        {
          isSuccess
            ? null
            : (
              <Footer
                showCheckbox
                tncChecked={accepted}
                setTncChecked={setAccepted}
              />
            )
        }
      </AuthenticateLayout>

    </>
  );
}
